// Apply a composer patch to a graph. Shared by the slice (for real commits)
// and the composer UI (for preview).

import type { Graph } from './api';
import type { GraphPatch, PatchNode, PatchEdge } from './composerTypes';

const genId = (prefix: string) =>
  `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

export function applyPatchToGraph(graph: Graph, patch: GraphPatch): Graph {
  let nodes = [...graph.nodes];
  let edges = [...graph.edges];

  if (patch.remove_node_ids?.length) {
    const kill = new Set(patch.remove_node_ids);
    nodes = nodes.filter((n) => !kill.has(n.id));
    edges = edges.filter((e) => !kill.has(e.source) && !kill.has(e.target));
  }
  if (patch.remove_edge_ids?.length) {
    const kill = new Set(patch.remove_edge_ids);
    edges = edges.filter((e) => !(e.id && kill.has(e.id)));
  }

  if (patch.update_nodes?.length) {
    nodes = nodes.map((n) => {
      const upd = patch.update_nodes!.find((u) => u.id === n.id);
      if (!upd) return n;
      const nextConfig = upd.replace_config
        ? upd.replace_config
        : { ...n.config, ...(upd.config_patch || {}) };
      return { ...n, type: upd.type || n.type, config: nextConfig };
    });
  }

  if (patch.add_nodes?.length) {
    const existingIds = new Set(nodes.map((n) => n.id));
    const autoPosition = (i: number) => ({
      x: 80 + ((nodes.length + i) % 4) * 260,
      y: 100 + Math.floor((nodes.length + i) / 4) * 140,
    });
    const fresh = patch.add_nodes
      .filter((n: PatchNode) => !existingIds.has(n.id))
      .map((n: PatchNode, i: number) => ({
        id: n.id,
        type: n.type,
        config: n.config || {},
        position: n.position || autoPosition(i),
      }));
    nodes = [...nodes, ...fresh];
  }

  if (patch.add_edges?.length) {
    const existing = new Set(edges.map((e) => `${e.source}->${e.target}`));
    const fresh = patch.add_edges
      .filter((e: PatchEdge) => !existing.has(`${e.source}->${e.target}`))
      .map((e: PatchEdge) => ({
        id: e.id || genId('e'),
        source: e.source,
        target: e.target,
      }));
    edges = [...edges, ...fresh];
  }

  const nextTrigger = patch.set_trigger !== undefined ? patch.set_trigger : graph.trigger;
  const nextDocsUrl = patch.set_docs_url !== undefined ? patch.set_docs_url : graph.docs_url;

  return { nodes, edges, trigger: nextTrigger, docs_url: nextDocsUrl };
}
